import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, CheckCircle2, ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const ForgotPassword = () => {
  const [username, setUsername] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/auth/password-reset-request`, { username: username.trim() });
      setSent(true);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-slate-200/60 shadow-lg">
        <CardContent className="p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Mail className="w-6 h-6 text-green-700" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {sent ? 'Check Your Email' : 'Forgot Password'}
            </h1>
          </div>

          {sent ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-slate-600 text-sm">
                If the username <strong>{username}</strong> exists and has an email on file, we've sent a password reset link.
              </p>
              <p className="text-slate-500 text-xs">Check your inbox and spam folder. The link expires in 1 hour.</p>
              <div className="pt-2">
                <Link to="/login" className="inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-800">
                  <ArrowLeft className="w-3 h-3" />Back to Login
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-slate-500 text-center">
                Enter your username and we'll send a reset link to the email on your account.
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Username</label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="rounded-xl"
                  required
                  autoFocus
                  data-testid="forgot-username-input"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full rounded-full bg-green-600 hover:bg-green-700"
                data-testid="send-reset-link-button">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : 'Send Reset Link'}
              </Button>
              <div className="text-center">
                <Link to="/login" className="inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-800">
                  <ArrowLeft className="w-3 h-3" />Back to Login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
